/**
 * projectionClient — typed wrappers for the cycle-102 sprint-3
 * Workspace ↔ Environment deep-link bridge.
 *
 * Two endpoints, both authenticated:
 *   - POST /api/v1/projection/investigation-link → mints opaque HS256 token
 *   - GET  /api/v1/projection/investigation-link/{token} → resolves
 *     to summary + 4 typed ports OR `redacted: true` per SDD §5.2.2.
 *
 * Both responses are runtime-validated via zod so the InvestigationNode
 * inspector can render REDACTED/error fallbacks per the graceful-
 * degradation matrix (SDD §6.4) when shapes drift.
 *
 * Surface documented in `frontend/src/api/README.md`.
 */

import { z } from 'zod';
import { apiClient } from './client';

// ─── Schemas ────────────────────────────────────────────────────────

export const InvestigationProjectionPortsSchema = z.object({
  evidence_bundle_ref: z.string(),
  claim_graph_state: z.enum([
    'empty',
    'drafting',
    'supported',
    'contested',
    'promoted',
  ]),
  corroboration_score: z.number().min(0).max(1),
  readiness: z.enum([
    'INSUFFICIENT',
    'ADEQUATE',
    'STRONG',
    'READY_TO_PROMOTE',
  ]),
});

export const InvestigationProjectionSummarySchema = z.object({
  investigation_id: z.string(),
  headline: z.string(),
  status: z.string(),
  inquiry_class: z.string().nullable().optional(),
  template_name: z.string().nullable().optional(),
  ports: InvestigationProjectionPortsSchema,
  workspace_url: z.string(),
});

export const ProjectionResolveResponseSchema = z.object({
  redacted: z.boolean(),
  summary: InvestigationProjectionSummarySchema.nullable(),
  request_access_url: z.string().nullable().optional(),
});

export const ProjectionLinkResponseSchema = z.object({
  token: z.string(),
  expires_at: z.string(),
});

export type InvestigationProjectionPorts = z.infer<
  typeof InvestigationProjectionPortsSchema
>;
export type InvestigationProjectionSummary = z.infer<
  typeof InvestigationProjectionSummarySchema
>;
export type ProjectionResolveResponse = z.infer<
  typeof ProjectionResolveResponseSchema
>;
export type ProjectionLinkResponse = z.infer<
  typeof ProjectionLinkResponseSchema
>;

// ─── Fetchers ──────────────────────────────────────────────────────

export async function createProjectionLink(
  investigationId: string,
): Promise<ProjectionLinkResponse> {
  const { data } = await apiClient.post('/api/v1/projection/investigation-link', {
    investigation_id: investigationId,
  });
  return ProjectionLinkResponseSchema.parse(data);
}

export async function resolveProjectionLink(
  token: string,
): Promise<ProjectionResolveResponse> {
  const { data } = await apiClient.get(
    `/api/v1/projection/investigation-link/${encodeURIComponent(token)}`,
  );
  return ProjectionResolveResponseSchema.parse(data);
}

// ─── Readiness helpers ─────────────────────────────────────────────

export const READINESS_LABEL: Record<
  InvestigationProjectionPorts['readiness'],
  string
> = {
  INSUFFICIENT: 'Insufficient',
  ADEQUATE: 'Adequate',
  STRONG: 'Strong',
  READY_TO_PROMOTE: 'Ready to promote',
};

export const CLAIM_GRAPH_LABEL: Record<
  InvestigationProjectionPorts['claim_graph_state'],
  string
> = {
  empty: 'Empty',
  drafting: 'Drafting',
  supported: 'Supported',
  contested: 'Contested',
  promoted: 'Promoted',
};
